import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/d8ttds9d9m2hsn3jsksg";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    if (!email.trim()) return;

    const formData = new FormData(form);
    // Remove honeypot before submitting
    formData.delete("website_alt");

    try {
      await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
    } catch {
      // Silently handle - still show success to user
    }
    setSubmitted(true);
    setEmail("");
  };

  return (
    <footer className="bg-foreground-950 text-background-50 rounded-t-3xl mx-4 md:mx-6 mt-16 md:mt-24 overflow-hidden relative">
      {/* Background watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.04]">
        <span className="font-heading text-[12rem] md:text-[18rem] whitespace-nowrap">ACBUY</span>
      </div>

      <div className="relative z-10 px-6 md:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-14">
          {/* Brand */}
          <div>
            <h3 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">ACBuySpreadsheet</h3>
            <p className="text-sm text-foreground-300 leading-relaxed">
              The smartest product tracking spreadsheet system. Track, compare, and manage your buying decisions with speed and clarity.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  Back to Home
                </Link>
              </li>
              <li>
                <Link to="/what-is-acbuyspreadsheet" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  What is ACBuySpreadsheet
                </Link>
              </li>
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">Follow</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i> Instagram
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-youtube-line text-lg"></i> YouTube
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-facebook-line text-lg"></i> Facebook
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-lg text-background-50 mb-4">Stay Updated</h4>
            <p className="text-sm text-foreground-300 mb-4 leading-relaxed">
              Get the latest product tracking tips and updates.
            </p>
            {submitted ? (
              <p className="text-sm text-primary-300 font-medium">Thanks for subscribing!</p>
            ) : (
              <form
                onSubmit={handleSubmit}
                action={FORM_SUBMIT_URL}
                method="POST"
                encType="application/x-www-form-urlencoded"
                data-readdy-form
                className="flex flex-col sm:flex-row gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-foreground-800/50 border border-foreground-700 rounded-full text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  data-readdy-hp
                />
                <button
                  type="submit"
                  className="w-10 h-10 flex items-center justify-center bg-primary-500 hover:bg-primary-400 rounded-full transition-colors duration-200 flex-shrink-0 cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-background-50"></i>
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-foreground-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} acbuy.yachts. All rights reserved.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-xs text-foreground-400 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Powered by kaspreadsheet.com
          </a>
        </div>
      </div>
    </footer>
  );
}